// Pure helpers for the Contacts list + contact page: filter state ⇄ URL, the
// Patients on Demand link, safe reading of the stored POD record, phone links,
// payload types and CSV.
//
// Free of browser globals (only the pure ./csv import) so the root vitest suite can
// load it. The data comes from the contact_directory / contact_overview Postgres
// functions (supabase/migrations/073_contacts_upgrade.sql); hooks are in queries.ts.

import { toCsv } from './csv';

// ── Payload types ───────────────────────────────────────────────────────────

export type ContactHealth = 'healthy' | 'watch' | 'attention';

export interface ContactDirectoryRow {
  email: string;
  name: string | null;
  company: string | null;
  phone: string | null;
  tier: string | null;
  is_existing_client: boolean;
  is_automated: boolean;
  is_edited: boolean;
  dnd: boolean;
  tickets: number;
  open: number;
  first_at: string | null;
  last_at: string | null;
  negative: number;
  breaches: number;
  health: ContactHealth;
  inbox_ids: string[];
}

export interface ContactDirectorySummary {
  contacts: number;
  clients: number;
  with_open: number;
  attention: number;
  automated: number;
}

export interface ContactDirectory {
  total: number;
  summary: ContactDirectorySummary;
  rows: ContactDirectoryRow[];
}

export interface ContactOverviewData {
  tickets: number;
  open: number;
  resolved: number;
  dismissed: number;
  first_at: string | null;
  last_at: string | null;
  breaches: number;
  breach_rate: number | null;
  fr_breach_rate: number | null;
  first_reply_median_min: number | null;
  first_reply_p90_min: number | null;
  resolution_median_min: number | null;
  health: ContactHealth;
  sentiment_last10: string[];
  categories: Array<{ key: string; count: number }>;
  inboxes: Array<{ id: string; name: string; color: string | null; is_confidential: boolean; count: number }>;
  assignees: Array<{ slack_id: string; count: number }>;
  inbound_msgs: number;
  outbound_msgs: number;
  monthly: Array<{ t: string; created: number; resolved: number }>;
  journeys: { count: number; latest_id: string | null; latest_at: string | null };
}

// ── Filters ─────────────────────────────────────────────────────────────────

export type ContactRelationship = 'all' | 'client' | 'prospect';
export type ContactTierFilter = 'any' | 'existing-high' | 'existing-standard' | 'none';
export type ContactSort = 'recent' | 'tickets' | 'open' | 'name' | 'attention';

export interface ContactFilters {
  q: string;
  relationship: ContactRelationship;
  tier: ContactTierFilter;
  /** Only contacts with open tickets. */
  openOnly: boolean;
  /** Include senders flagged as automated (hidden by default). */
  automated: boolean;
  /** Only contacts someone edited by hand (pinned against POD sync). */
  edited: boolean;
  /** gmail_inboxes id, '' = all the viewer's inboxes. */
  inbox: string;
  sort: ContactSort;
}

export const DEFAULT_CONTACT_FILTERS: ContactFilters = {
  q: '',
  relationship: 'all',
  tier: 'any',
  openOnly: false,
  automated: false,
  edited: false,
  inbox: '',
  sort: 'recent',
};

export const CONTACT_PAGE_SIZE = 50;
export const CONTACT_CSV_MAX = 5000;

export const CONTACT_SORT_OPTIONS: Array<{ key: ContactSort; label: string; hint: string }> = [
  { key: 'recent', label: 'Recent activity', hint: 'Emailed most recently' },
  { key: 'tickets', label: 'Most tickets', hint: 'Longest histories first' },
  { key: 'open', label: 'Most open', hint: 'Most unresolved tickets' },
  { key: 'attention', label: 'Needs attention', hint: 'Unhappy or SLA-breaching first' },
  { key: 'name', label: 'Name A–Z', hint: 'Alphabetical' },
];

export const CONTACT_TIER_OPTIONS: Array<{ key: ContactTierFilter; label: string }> = [
  { key: 'any', label: 'Any tier' },
  { key: 'existing-high', label: 'High' },
  { key: 'existing-standard', label: 'Standard' },
  { key: 'none', label: 'No tier' },
];

export const CONTACT_HEALTH_META: Record<ContactHealth, { label: string; tone: 'critical' | 'warning' | 'success' }> = {
  attention: { label: 'Needs attention', tone: 'critical' },
  watch: { label: 'Watch', tone: 'warning' },
  healthy: { label: 'Healthy', tone: 'success' },
};

const MAX_QUERY_LEN = 80;
const RELATIONSHIPS: ContactRelationship[] = ['all', 'client', 'prospect'];
const TIERS: ContactTierFilter[] = ['any', 'existing-high', 'existing-standard', 'none'];
const SORTS: ContactSort[] = ['recent', 'tickets', 'open', 'name', 'attention'];

type ParamsLike = { get(key: string): string | null };

/** Read filter state from the URL; anything missing or invalid falls back to the default. */
export function parseContactFilters(params: ParamsLike): ContactFilters {
  const rel = params.get('rel') as ContactRelationship | null;
  const tier = params.get('tier') as ContactTierFilter | null;
  const sort = params.get('sort') as ContactSort | null;
  return {
    q: (params.get('q') ?? '').trim().slice(0, MAX_QUERY_LEN),
    relationship: rel && RELATIONSHIPS.includes(rel) ? rel : DEFAULT_CONTACT_FILTERS.relationship,
    tier: tier && TIERS.includes(tier) ? tier : DEFAULT_CONTACT_FILTERS.tier,
    openOnly: params.get('open') === '1',
    automated: params.get('automated') === '1',
    edited: params.get('edited') === '1',
    inbox: (params.get('inbox') ?? '').trim().slice(0, 64),
    sort: sort && SORTS.includes(sort) ? sort : DEFAULT_CONTACT_FILTERS.sort,
  };
}

/** The non-default filters as URL params (defaults dropped → clean URLs). */
export function serializeContactFilters(f: ContactFilters): Record<string, string> {
  const out: Record<string, string> = {};
  const q = f.q.trim().slice(0, MAX_QUERY_LEN);
  if (q) out.q = q;
  if (f.relationship !== 'all') out.rel = f.relationship;
  if (f.tier !== 'any') out.tier = f.tier;
  if (f.openOnly) out.open = '1';
  if (f.automated) out.automated = '1';
  if (f.edited) out.edited = '1';
  if (f.inbox) out.inbox = f.inbox;
  if (f.sort !== 'recent') out.sort = f.sort;
  return out;
}

/** True when something other than the sort differs from the default (drives "Clear filters"). */
export function hasActiveContactFilters(f: ContactFilters): boolean {
  return Object.keys(serializeContactFilters(f)).some((k) => k !== 'sort');
}

/**
 * PostgREST `or()` filter for a contacts_cache search box (email, name). Characters
 * that are syntax in a filter string (comma, parens, quotes) or wildcards the person
 * didn't mean are dropped, so typing "Smith, Jane" can't 400 the request.
 */
export function contactSearchClause(q: string): string | null {
  const term = q
    .replace(/[,()"*%\\_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_QUERY_LEN);
  if (!term) return null;
  const pat = `*${term}*`;
  return `email.ilike.${pat},full_name.ilike.${pat}`;
}

/**
 * The subject filter box on a contact's page. Strips characters that are syntax
 * in a PostgREST filter or LIKE wildcards, collapses whitespace, caps the length.
 * (This used to be inline in queries.ts with `/s+/` instead of `/\s+/`, which
 * turned every letter "s" into a space: "subscription" searched for "ub cription".)
 */
export function contactTicketSubjectTerm(q: string): string {
  return q
    .replace(/[%*,()"\\_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
}

// ── Patients on Demand ──────────────────────────────────────────────────────

/** The team's Patients on Demand address. */
export const POD_BASE_URL = 'https://app.patientsondemand.com';

function str(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t ? t : null;
}

/** Link to the contact's record in Patients on Demand, or null when we don't
 *  have both the location and the contact id. */
export function podContactUrl(contact: { ghl_contact_id?: string | null; raw?: unknown } | null | undefined): string | null {
  if (!contact) return null;
  const id = str(contact.ghl_contact_id);
  const raw = (contact.raw ?? {}) as Record<string, unknown>;
  const loc = str(raw.locationId);
  if (!id || !loc) return null;
  return `${POD_BASE_URL}/v2/location/${encodeURIComponent(loc)}/contacts/detail/${encodeURIComponent(id)}`;
}

export interface ContactProfile {
  phone: string | null;
  company: string | null;
  website: string | null;
  address: string | null;
  timezone: string | null;
  tags: string[];
  source: string | null;
  /** ISO timestamp the contact was added in POD. */
  dateAdded: string | null;
  dnd: boolean;
}

/** Safe reading of the stored POD record (`contacts_cache.raw`, untyped JSON). */
export function contactProfile(raw: unknown): ContactProfile {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const tags = Array.isArray(r.tags) ? r.tags.filter((t): t is string => typeof t === 'string' && t.trim() !== '').map((t) => t.trim()) : [];
  const address = [str(r.address1), str(r.city), str(r.state), str(r.postalCode), str(r.country)].filter(Boolean).join(', ');
  const dateAdded = str(r.dateAdded);
  return {
    phone: str(r.phone),
    company: str(r.companyName),
    website: str(r.website),
    address: address || null,
    timezone: str(r.timezone),
    tags,
    source: str(r.source),
    dateAdded: dateAdded && !Number.isNaN(Date.parse(dateAdded)) ? dateAdded : null,
    dnd: r.dnd === true,
  };
}

/** `tel:` link for a phone number, or null when it doesn't look like one. */
export function telHref(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/[^\d]/g, '');
  if (digits.length < 6) return null;
  const plus = phone.trim().startsWith('+') ? '+' : '';
  return `tel:${plus}${digits}`;
}

/** Display label for a stored tier value. */
export function tierLabel(tier: string | null | undefined): string | null {
  if (!tier) return null;
  if (tier === 'existing-high') return 'High';
  if (tier === 'existing-standard') return 'Standard';
  return tier.replace(/^existing-/, '').replace(/[-_]+/g, ' ');
}

// ── CSV ─────────────────────────────────────────────────────────────────────

export function contactsToCsv(rows: ContactDirectoryRow[]): string {
  return toCsv(
    [
      'Name',
      'Email',
      'Company',
      'Phone',
      'Relationship',
      'Tier',
      'Automated sender',
      'Tickets',
      'Open',
      'First contact',
      'Last contact',
      'Negative sentiment',
      'SLA breaches',
      'Health',
    ],
    rows.map((r) => [
      r.name ?? '',
      r.email,
      r.company ?? '',
      r.phone ?? '',
      r.is_existing_client ? 'Client' : 'Prospect',
      tierLabel(r.tier) ?? '',
      r.is_automated ? 'Yes' : '',
      r.tickets,
      r.open,
      r.first_at ? r.first_at.slice(0, 10) : '',
      r.last_at ? r.last_at.slice(0, 10) : '',
      r.negative,
      r.breaches,
      CONTACT_HEALTH_META[r.health]?.label ?? r.health,
    ]),
  );
}
